<template>
    <div
        ref="rootElement"
        :class="classSwitcher"
        class="locale-switcher"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
    >
        <button
            ref="menuButton"
            :aria-expanded="isOpen"
            :aria-label="triggerAriaLabel"
            :data-qa-trigger-currency="country?.currency"
            :data-qa-trigger-iso="country?.isoCode"
            aria-haspopup="dialog"
            class="trigger"
            type="button"
            @click.prevent.stop="toggle"
        >
            <Flag
                v-if="country"
                :country="country.isoCode"
            />

            <span
                aria-hidden="true"
                class="divider"
            ></span>

            <span class="currency">
                {{ currency }}
                {{ getCurrencySymbol(currency) }}
            </span>

            <span
                aria-hidden="true"
                class="chevron"
            ></span>
        </button>

        <div
            v-if="isOpen"
            class="panel-host"
        >
            <div
                aria-hidden="true"
                class="panel-backdrop"
                @click="close"
            />

            <div
                ref="panelElement"
                :class="{ 'is-currency-open': isCurrencyPanelOpen }"
                aria-label="Country and currency selection"
                aria-modal="true"
                class="panel"
                role="dialog"
                @keydown.escape.prevent.stop="handlePanelEscape"
                @keydown.down.prevent.stop="focusNext"
                @keydown.up.prevent.stop="focusPrevious"
            >
                <header class="drawer-header">
                    <div class="drawer-header-start">
                        <button
                            v-if="isCurrencyPanelOpen"
                            aria-label="Back to countries"
                            class="back-nav"
                            type="button"
                            @click="closeCurrencyPanel"
                        >
                            <Icon
                                class="icon"
                                icon="angle-left"
                            />

                            <span class="title">Currency</span>
                        </button>

                        <h2
                            v-else
                            class="drawer-title"
                        >
                            Country and currency
                        </h2>
                    </div>

                    <button
                        aria-label="Close"
                        class="drawer-close"
                        type="button"
                        @click="closeAndFocusTrigger"
                    ></button>
                </header>

                <div
                    :class="{
                        'is-currency-open': isCurrencyPanelOpen,
                    }"
                    class="panel-inner"
                >
                    <CurrencyPanel
                        v-show="isCurrencyPanelOpen"
                        :currencies="currencies"
                        :currency-selection="currency"
                        @back="closeCurrencyPanel"
                        @select-currency="selectCurrency"
                    />

                    <CountryPanel
                        v-if="country"
                        v-show="!isCurrencyPanelOpen"
                        ref="countryPanel"
                        :is-currency-panel-open="isCurrencyPanelOpen"
                        :selection="country"
                        :shipping-countries="countries"
                        @select-country="selectCountry"
                        @open-currency-panel="openCurrencyPanel"
                        @escape-search="closeAndFocusTrigger"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, useTemplateRef } from 'vue'
import { onClickOutside } from '@vueuse/core'
import CountryPanel from '@/components/app/locale/CountryPanel.vue'
import CurrencyPanel from '@/components/app/locale/CurrencyPanel.vue'
import { useTranslator } from '@/i18n'
import Icon from '@/components/ui/icon/Icon.vue'
import Flag from '@/components/Flag.vue'
import type { AppCurrency } from '@/plugins/currency-types'
import { getRequestAnimationFrame, getWindow } from '@/helpers/globals'
import { useCountryPlugin } from '@/plugins/country.ts'
import { useCurrency, useCurrencyPlugin } from '@/plugins/currency.ts'
import type { ShippingCountryFragment } from '@/graphql/api'

const { dropdownDirection = 'up' } = defineProps<{
    dropdownDirection?: 'up' | 'down'
}>()

const countryPlugin = useCountryPlugin()

const { country, countries, selectCountry: _selectCountry } = countryPlugin

const currencyPlugin = useCurrencyPlugin()

const currency = useCurrency()
const { selectCurrency: _selectCurrency, currencies } = currencyPlugin

const { getCurrencySymbol, getCountryName, getCurrencyName } = useTranslator()

const isOpen = ref(false)
const isCurrencyPanelOpen = ref(false)
const suppressMouseLeaveClose = ref(false)

const triggerAriaLabel = computed((): string => {
    if (!country.value) {
        return ''
    }
    const countryName = getCountryName(country.value?.isoCode)
    const currencyLabel = getCurrencyName(currency.value)

    return `${countryName}, ${currencyLabel}`
})

const classSwitcher = computed(() => ({
    'is-open': isOpen.value,
    [dropdownDirection]: true,
}))

const openCurrencyPanel = () => {
    suppressMouseLeaveClose.value = true
    isCurrencyPanelOpen.value = true
    nextTick(() => {
        focusItem(0)
        getRequestAnimationFrame()(() => {
            suppressMouseLeaveClose.value = false
        })
    })
}

const closeCurrencyPanel = () => {
    isCurrencyPanelOpen.value = false
}

const selectCurrency = (currency: AppCurrency) => {
    _selectCurrency(currency)
    closeCurrencyPanel()
}

const selectCountry = (
    _countryCode: ShippingCountryFragment['countries'][number]
) => {
    _selectCountry(_countryCode.isoCode)

    if (_countryCode.currency) {
        _selectCurrency(_countryCode.currency)
    }

    close()
    menuButton.value?.focus()
}

const rootElement = useTemplateRef<HTMLDivElement>('rootElement')
const menuButton = useTemplateRef<HTMLButtonElement>('menuButton')
const panelElement = useTemplateRef<HTMLDivElement>('panelElement')
const countryPanel =
    useTemplateRef<InstanceType<typeof CountryPanel>>('countryPanel')
const focusedIndex = ref(0)

const getFocusableOptions = (): HTMLElement[] => {
    if (!panelElement.value) {
        return []
    }

    const viewSelector = isCurrencyPanelOpen.value
        ? '.currency-panel'
        : '.country-panel'

    return Array.from(
        panelElement.value.querySelectorAll<HTMLElement>(
            `${viewSelector} .focusable, ${viewSelector} .currency-footer`
        )
    )
}

const focusItem = (index: number) => {
    const elements = getFocusableOptions()
    const count = elements.length

    if (count === 0) {
        return
    }

    if (index < 0) {
        focusedIndex.value = count - 1
    } else if (index >= count) {
        focusedIndex.value = 0
    } else {
        focusedIndex.value = index
    }

    elements[focusedIndex.value]?.focus()
}

const focusNext = () => focusItem(focusedIndex.value + 1)
const focusPrevious = () => focusItem(focusedIndex.value - 1)

const focusSearchInput = () => {
    countryPanel.value?.focusSearchInput()
}

const toggle = () => {
    isOpen.value = !isOpen.value

    if (isOpen.value) {
        if (!isCurrencyPanelOpen.value) {
            focusSearchInput()
        }
    } else {
        isCurrencyPanelOpen.value = false
        countryPanel.value?.resetSearch()
    }
}

const supportsHoverOpen = (): boolean =>
    getWindow()?.matchMedia('(hover: hover) and (min-width: 640px)').matches ??
    false

const handleMouseEnter = () => {
    if (supportsHoverOpen()) {
        open()
    }
}

const open = () => {
    isOpen.value = true
}

const handleMouseLeave = (event: MouseEvent) => {
    if (!supportsHoverOpen() || suppressMouseLeaveClose.value) {
        return
    }

    const relatedTarget = event.relatedTarget

    if (
        relatedTarget instanceof Node &&
        rootElement.value?.contains(relatedTarget)
    ) {
        return
    }

    close()
}

const close = () => {
    isOpen.value = false
    isCurrencyPanelOpen.value = false
    countryPanel.value?.resetSearch()
}

const handlePanelEscape = () => {
    if (isCurrencyPanelOpen.value) {
        closeCurrencyPanel()

        return
    }

    closeAndFocusTrigger()
}

const closeAndFocusTrigger = () => {
    close()
    menuButton.value?.focus()
}

onClickOutside(rootElement, () => {
    if (suppressMouseLeaveClose.value) {
        return
    }

    close()
})
</script>

<style lang="scss" scoped>
.locale-switcher {
    position: relative;
    font-family: $effra-font-stack;

    .trigger {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        min-height: 30px;
        padding: 0 10px;
        border: none;
        background: transparent;
        color: $white;
        font-family: inherit;
        font-size: 0.8125rem;
        font-weight: 400;
        letter-spacing: normal;
        text-transform: none;
        cursor: pointer;
        user-select: none;

        @include min-width(1024) {
            min-height: 36px;
            padding: 0 12px;
            border: 1px solid transparent;
            border-radius: var(--border-radius-m, 8px);
            @include default-transition();

            @include hover() {
                border-color: $grey-300;
                background: rgba($white, 0.5);
            }
        }

        .flag {
            flex-shrink: 0;
            font-size: 1rem;
            line-height: 1;
        }

        .divider {
            flex-shrink: 0;
            align-self: stretch;
            width: 1px;
            margin: 4px 0;
            background: currentColor;
            opacity: 0.25;
        }

        .currency {
            flex-shrink: 0;
            font-size: 0.8125rem;
            font-weight: 400;
            letter-spacing: 0.02em;
            white-space: nowrap;
        }

        .chevron {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-family: $icon-font;
            font-weight: 600;
            font-size: 11px;
            line-height: 1;

            &::before {
                content: $icon-angle-down;
            }
        }
    }

    .panel-host {
        @include fixed;
        z-index: 1000;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        pointer-events: none;

        @include min-width(640) {
            position: static;
            inset: auto;
            z-index: auto;
            display: contents;
            flex-direction: unset;
            justify-content: unset;
            pointer-events: auto;
        }

        .panel-backdrop {
            @include fixed;
            z-index: 0;
            display: block;
            background: rgba($black, 0.45);
            pointer-events: auto;

            @include min-width(640) {
                display: none;
            }
        }

        .panel {
            position: relative;
            z-index: 1;
            top: auto;
            right: auto;
            bottom: 0;
            left: 0;
            display: flex;
            flex-direction: column;
            width: 100%;
            padding: 0;
            margin: 0;
            border: none;
            background: $white;
            border-radius: 16px 16px 0 0;
            box-shadow: 0 -8px 32px rgba($black, 0.16);
            overflow: hidden;
            pointer-events: auto;
            animation: locale-drawer-slide-up 0.28s ease-out;
            max-height: min(85vh, 680px);

            .panel-inner {
                display: flex;
                flex-direction: column;
                flex: 1 1 auto;
                min-height: 0;
                border: none;
                border-radius: 0;
                background: transparent;
                box-shadow: none;

                &.is-currency-open {
                    min-height: 0;
                }
            }

            @include min-width(640) {
                position: absolute;
                z-index: 200;
                right: 0;
                left: auto;
                bottom: auto;
                top: auto;
                display: block;
                flex-direction: unset;
                width: min(100vw - 40px, 280px);
                max-height: min(80vh, 440px);
                padding: unset;
                margin: unset;
                background: transparent;
                border-radius: 0;
                box-shadow: none;
                animation: none;

                &:not(.is-currency-open) {
                    max-height: min(80vh, 440px);
                }

                &.is-currency-open {
                    max-height: min(85vh, 500px);
                }

                .panel-inner {
                    display: flex;
                    flex-direction: column;
                    flex: unset;
                    max-height: inherit;
                    min-height: 0;
                    border: 1px solid $grey-200;
                    border-radius: var(--border-radius-m, 8px);
                    background: $white;
                    box-shadow: 0 8px 24px rgba($black, 0.12);

                    @include min-width(1024) {
                        border-radius: 0;
                    }

                    &.is-currency-open {
                        min-height: calc(
                            2.75rem + 0.75rem + 7 * 2.875rem + 1.75rem + 1rem
                        );
                    }
                }
            }

            @include min-width(1024) {
                width: 320px;
                max-height: min(70vh, 460px);

                &:not(.is-currency-open) {
                    max-height: min(70vh, 460px);
                }

                &.is-currency-open {
                    max-height: min(75vh, 520px);
                }

                .panel-inner.is-currency-open {
                    min-height: calc(
                        2.875rem + 0.75rem + 7 * 3.125rem + 1.75rem + 1.25rem
                    );
                }
            }
        }
    }

    .panel-backdrop {
        display: none;
    }

    .panel {
        text-align: left;
        text-transform: none;
        letter-spacing: normal;

        @include min-width(640) {
            position: absolute;
            z-index: 200;
            right: 0;
            width: min(100vw - 40px, 280px);
            max-height: min(80vh, 440px);
            overflow: hidden;
        }

        @include min-width(1024) {
            width: 320px;
            max-height: min(70vh, 460px);
        }

        &.is-currency-open {
            @include min-width(640) {
                max-height: min(85vh, 500px);
            }

            @include min-width(1024) {
                max-height: min(75vh, 520px);
            }
        }

        .drawer-header {
            display: flex;
            flex-shrink: 0;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 14px calc(var(--spacing-16, 16px) + 4px);
            border-bottom: 1px solid $grey-200;
            background: $white;

            @include min-width(640) {
                display: none;
            }

            .drawer-header-start {
                display: flex;
                flex: 1 1 auto;
                align-items: center;
                min-width: 0;
            }

            .back-nav {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                max-width: 100%;
                min-height: 32px;
                padding: 0;
                border: none;
                border-radius: var(--border-radius-m, 8px);
                background: transparent;
                font-family: inherit;
                text-align: left;
                color: $slate;
                cursor: pointer;
                @include default-transition();

                &:focus {
                    outline: none;
                }

                &:focus-visible {
                    outline: 2px solid $blue;
                    outline-offset: 2px;
                }

                @include hover() {
                    color: $blue;

                    .title {
                        color: $blue;
                    }
                }

                .icon {
                    display: inline-flex;
                    flex-shrink: 0;
                    align-items: center;
                    justify-content: center;
                    width: 32px;
                    height: 32px;
                    font-size: 12px;
                }

                .title {
                    font-family: $effra-font-stack;
                    font-size: 1rem;
                    font-weight: 400;
                    line-height: 1.2;
                    color: $slate;
                    text-transform: none;
                    letter-spacing: normal;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
            }

            .drawer-title {
                margin: 0;
                font-family: $effra-font-stack;
                font-size: 1rem;
                font-weight: 400;
                line-height: 1.2;
                color: $slate;
                text-transform: none;
                letter-spacing: normal;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .drawer-close {
                position: relative;
                top: auto;
                right: auto;
                flex-shrink: 0;
                width: 36px;
                height: 36px;
                border: none;
                background: transparent;
                cursor: pointer;
                line-height: 36px;
                text-align: center;
                font-size: 0;

                &::before {
                    font-family: $icon-font;
                    content: $icon-close;
                    font-size: 18px;
                    color: $slate;
                }
            }
        }

        .panel-inner {
            position: relative;
            display: flex;
            flex-direction: column;
            max-height: inherit;
            min-height: 0;
            overflow: hidden;

            &.is-currency-open {
                @include min-width(640) {
                    min-height: calc(
                        2.75rem + 0.75rem + 7 * 2.875rem + 1.75rem + 1rem
                    );
                }

                @include min-width(1024) {
                    min-height: calc(
                        2.875rem + 0.75rem + 7 * 3.125rem + 1.75rem + 1.25rem
                    );
                }
            }
        }
    }

    &.down .panel,
    &:not(.down) .panel {
        @include min-width(640) {
            border: none;
            border-radius: 0;
            background: transparent;
            box-shadow: none;
            overflow: visible;

            .panel-inner {
                border: 1px solid $grey-200;
                border-radius: var(--border-radius-m, 8px);
                background: $white;
                box-shadow: 0 8px 24px rgba($black, 0.12);

                @include min-width(1024) {
                    border-radius: 0;
                }
            }
        }
    }

    &.down .panel {
        @include min-width(640) {
            top: 100%;
            bottom: auto;
            padding-top: 8px;
            margin-top: 0;
        }
    }

    &:not(.down) .panel {
        @include min-width(640) {
            bottom: 100%;
            top: auto;
            padding-bottom: 8px;
            margin-bottom: 0;
        }
    }

    @include min-width(1024) {
        .trigger {
            color: $slate;
        }

        &.down .panel {
            right: 0;
            left: auto;
        }
    }
}

@keyframes locale-drawer-slide-up {
    from {
        transform: translateY(100%);
    }

    to {
        transform: translateY(0);
    }
}
</style>

<style lang="scss">
body:has(.locale-switcher.is-open) {
    overflow: hidden;

    @include min-width(640) {
        overflow: auto;
    }
}
</style>
